import { useRef, useState } from "react";
import { Play, Pause, AudioLines } from "lucide-react";

// Plays a real recording when `src` is provided; otherwise renders a calm
// "coming soon" placeholder without fabricating audio.
export default function AudioPlayer({ label = "Audio recitation", src = "", available }) {
  const hasSrc = Boolean(src);
  const canPlay = available ?? hasSrc;
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [time, setTime] = useState("0:00");

  const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) { a.play(); setPlaying(true); } else { a.pause(); setPlaying(false); }
  };

  return (
    <div data-testid="audio-player" className="flex items-center gap-4 bg-[var(--saffron-dark)] px-5 py-4 text-[#f7f3ec]">
      {hasSrc && (
        <audio
          ref={audioRef}
          src={src}
          preload="metadata"
          onTimeUpdate={(e) => {
            const a = e.currentTarget;
            setProgress(a.duration ? (a.currentTime / a.duration) * 100 : 0);
            setTime(fmt(a.currentTime));
          }}
          onEnded={() => { setPlaying(false); setProgress(0); setTime("0:00"); }}
        />
      )}
      <button
        onClick={toggle}
        disabled={!canPlay}
        data-testid="audio-play-btn"
        aria-label={playing ? "Pause audio" : "Play audio"}
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[var(--gold)] text-[var(--gold)] transition-colors hover:bg-[var(--gold)] hover:text-[var(--ink)] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-transparent disabled:hover:text-[var(--gold)]"
      >
        {playing ? <Pause className="h-4 w-4" fill="currentColor" /> : <Play className="h-4 w-4" fill="currentColor" />}
      </button>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <AudioLines className="h-3.5 w-3.5 text-[var(--gold)]" />
          <p className="truncate text-sm">{label}</p>
        </div>
        <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-white/15">
          <div className="h-full bg-[var(--gold)] transition-[width] duration-200" style={{ width: `${progress}%` }} />
        </div>
      </div>
      <span className="shrink-0 text-xs text-white/55">{canPlay ? time : "Coming soon"}</span>
    </div>
  );
}
